import {
    ChannelUpdatedEvent,
    EmbedEvent,
    EmbedEvents,
} from 'embed/types/events'

import {
    NinchatChannelJoinedEvent, NinchatChannelUpdatedEvent, NinchatEventType,
} from 'api/types/events'

import {NinchatEnvironmentConfig} from 'embed/types'
import {RootState} from 'store'
import {channelJoined} from 'api/event/channelJoined'
import {channelUpdated} from 'api/event/channelUpdated'
import {config} from 'embed/listeners'
import {extractProperties} from 'api/event'
import {isVideoChannel} from 'utils/channels'
import {jitsiRequestAutoJoin} from 'actions/jitsi'
import {messageReceived} from 'api/event/messageReceived'
import {ninchatEventSlice} from 'api/slice'
import {push} from 'navigation/actions'
import {startAppListening} from 'middlewares/listener'
import {updateSession} from 'api/action/updateSession'

type Effect = Parameters<typeof startAppListening>[0]['effect']
type ListenerApi = Parameters<Effect>[1]
type Predicate = Parameters<typeof startAppListening>[0]['predicate']

const sessionStatusUpdatedType = `${ninchatEventSlice.actions.session_status_updated}`
const sessionUpdatedType = `${updateSession.fulfilled.type}`

const getQueue = (state: RootState, queueId?: string) => {
    if (!queueId) {
        return undefined
    }
    return state.ninchat.user_queues?.[queueId] ?? state.ninchat._queues?.[queueId]
}

const shouldRequestCustomerAutoJoin = ({
    channel_id,
    conf,
    listenerApi,
}: {
    channel_id: string
    conf: NinchatEnvironmentConfig
    listenerApi: Pick<ListenerApi, 'dispatch' | 'getState'>
}) => {
    if (conf?.variant !== 'queue') {
        return false
    }

    const channel = listenerApi.getState().ninchat.user_channels[channel_id]
    if (!channel?.channel_attrs?.audience_id) {
        // not an audience channel
        return false
    }
    if (!isVideoChannel(channel)) {
        // not a video channel
        return false
    }
    const queueId = channel.channel_attrs.queue_id
    const queue = getQueue(listenerApi.getState(), queueId)
    return queue?.queue_attrs?.video_autojoin === true
}

export const channelUpdatedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (action, listenerApi) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const {
            channel_id, channel_attrs, realm_id,
        } = action.payload as NinchatChannelUpdatedEvent

        if (!channel_attrs.audience_id) {
            return
        }

        const channel_status = listenerApi.getState().ninchat.user_channels[channel_id]?.channel_status

        const event: ChannelUpdatedEvent = {
            channel_attrs: {
                audience_id: channel_attrs.audience_id,
                closed: channel_attrs.closed,
                name: channel_attrs.name,
                queue_id: channel_attrs.queue_id,
            },
            channel_id,
            channel_status,
            event: EmbedEvent.ChannelUpdated,
            realm_id,
            targetOrigin,
        }

        sendToParent(event)
    },
    predicate: (action) => action.type === channelUpdated.fulfilled.type,
})

export const channelStatusListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (_action, listenerApi) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const prevChannels = listenerApi.getOriginalState().ninchat.user_channels
        const nextChannels = listenerApi.getState().ninchat.user_channels

        Object.entries(nextChannels).forEach(([channel_id, channel]) => {
            if (!channel.channel_attrs?.audience_id) {
                return
            }

            if (prevChannels[channel_id]?.channel_status === channel.channel_status) {
                return
            }

            const {
                channel_attrs: {
                    audience_id, queue_id, name, closed,
                },
                channel_status,
                realm_id,
            } = channel

            const event: ChannelUpdatedEvent = {
                channel_attrs: {
                    audience_id,
                    closed,
                    name,
                    queue_id,
                },
                channel_id,
                channel_status,
                event: EmbedEvent.ChannelUpdated,
                realm_id,
                targetOrigin,
            }

            sendToParent(event)
        })
    },
    predicate: (action) => action.type === messageReceived.fulfilled.type ||
        action.type === sessionStatusUpdatedType || action.type === sessionUpdatedType,
})

export const channelJoinedListener = (conf: NinchatEnvironmentConfig, sendToParent: (event: EmbedEvents) => void) => ({
    actionCreator: channelJoined.fulfilled,
    effect: (action: ReturnType<typeof channelJoined.fulfilled>, listenerApi: ListenerApi) => {
        const targetOrigin = config.get('targetOrigin')
        listenerApi.cancelActiveListeners()
        const payload = Array.isArray(action.payload)
            ? action.payload.find((p) => p.event === NinchatEventType.NinchatChannelJoinedEvent)
            : action.payload
        const {channel_id} = extractProperties<NinchatChannelJoinedEvent>(payload)

        if (shouldRequestCustomerAutoJoin({
            channel_id,
            conf,
            listenerApi,
        })) {
            listenerApi.dispatch(jitsiRequestAutoJoin(channel_id))
        }

        const {
            audience_transferred, audience_id,
        } =
        // eslint-disable-next-line no-extra-parens
        (action.payload as NinchatChannelJoinedEvent)?.channel_attrs || {}
        if (audience_transferred) {
            sendToParent({
                audience_id: audience_id || '',
                audience_transferred,
                channel_id,
                event: EmbedEvent.AudienceTransferred,
                targetOrigin: targetOrigin || '',
            })

            sendToParent({
                event: EmbedEvent.EmbedSound,
                sound: 'audienceTransferred',
                targetOrigin: targetOrigin || '',
            })
        } else {
            sendToParent({
                audience_id: audience_id || '',
                channel_id,
                event: EmbedEvent.AudienceStarted,
                targetOrigin: targetOrigin || '',
            })

            sendToParent({
                event: EmbedEvent.EmbedSound,
                sound: 'audienceStarted',
                targetOrigin: targetOrigin || '',
            })
        }

        listenerApi.dispatch(push({
            name: 'EmbedChannel',
            params: {channel_id},
        }))
    },
})

export const start = (conf: NinchatEnvironmentConfig, sendToParent: (event: EmbedEvents) => void) => [
    startAppListening(channelUpdatedListener(sendToParent)),
    startAppListening(channelStatusListener(sendToParent)),
    // channel navigation redirect only applies to 'queue'/'channel' variants
    ...conf?.variant === 'queue' || conf?.variant === 'channel'
        ? [startAppListening(channelJoinedListener(conf, sendToParent))]
        : [],
]
